import type { MeshKind } from "./types.ts";

export interface Geometry { positions: Float32Array; normals: Float32Array; uvs: Float32Array; indices: Uint32Array }

function build(pos: number[], nrm: number[], uv: number[], idx: number[]): Geometry {
  return { positions: new Float32Array(pos), normals: new Float32Array(nrm), uvs: new Float32Array(uv), indices: new Uint32Array(idx) };
}

/** Unit quad in the XY plane, centered, facing +z. UV origin at the top-left. */
export function quad(): Geometry {
  return build([-.5, -.5, 0, .5, -.5, 0, .5, .5, 0, -.5, .5, 0], [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1],
               [0, 1, 1, 1, 1, 0, 0, 0], [0, 1, 2, 0, 2, 3]);
}

function cube(size: number): Geometry {
  const h = size / 2, pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  const faces: [number[], number[], number[]][] = [   // normal, u axis, v axis
    [[0, 0, 1], [1, 0, 0], [0, 1, 0]], [[0, 0, -1], [-1, 0, 0], [0, 1, 0]],
    [[1, 0, 0], [0, 0, -1], [0, 1, 0]], [[-1, 0, 0], [0, 0, 1], [0, 1, 0]],
    [[0, 1, 0], [1, 0, 0], [0, 0, -1]], [[0, -1, 0], [1, 0, 0], [0, 0, 1]]];
  for (const [n, u, v] of faces) {
    const base = pos.length / 3;
    for (const [su, sv] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      pos.push(...[0, 1, 2].map((i) => (n![i]! + su * u![i]! + sv * v![i]!) * h));
      nrm.push(...n!); uv.push((su + 1) / 2, (1 - sv) / 2);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return build(pos, nrm, uv, idx);
}

function sphere(size: number, seg = 48, rings = 32): Geometry {
  const r = size / 2, pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let i = 0; i <= rings; i++) {
    const v = i / rings, phi = v * Math.PI;
    for (let j = 0; j <= seg; j++) {
      const u = j / seg, th = u * Math.PI * 2;
      const n = [Math.sin(phi) * Math.cos(th), Math.cos(phi), Math.sin(phi) * Math.sin(th)];
      pos.push(n[0]! * r, n[1]! * r, n[2]! * r); nrm.push(...n); uv.push(u, v);
    }
  }
  for (let i = 0; i < rings; i++) for (let j = 0; j < seg; j++) {
    const a = i * (seg + 1) + j, b = a + seg + 1;
    idx.push(a, a + 1, b, a + 1, b + 1, b);
  }
  return build(pos, nrm, uv, idx);
}

function torus(size: number, seg = 72, tube = 36): Geometry {
  const R = size * 0.34, r = size * 0.16, pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let i = 0; i <= seg; i++) for (let j = 0; j <= tube; j++) {
    const u = (i / seg) * Math.PI * 2, v = (j / tube) * Math.PI * 2;
    const cx = Math.cos(u), cz = Math.sin(u);
    const n = [Math.cos(v) * cx, Math.sin(v), Math.cos(v) * cz];
    pos.push((R + r * Math.cos(v)) * cx, r * Math.sin(v), (R + r * Math.cos(v)) * cz); nrm.push(...n);
    uv.push(i / seg, j / tube);
  }
  for (let i = 0; i < seg; i++) for (let j = 0; j < tube; j++) {
    const a = i * (tube + 1) + j, b = a + tube + 1;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
  return build(pos, nrm, uv, idx);
}

/** Cylinder (topRadiusFactor=1) or cone (0): axis along Y, height = size, radius = size/2. */
function lathe(size: number, topFactor: number, seg = 64): Geometry {
  const r = size / 2, h = size / 2, pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  const slope = (r * (1 - topFactor)) / size;                      // side normal tilt
  for (let j = 0; j <= seg; j++) {
    const u = j / seg, th = u * Math.PI * 2, c = Math.cos(th), s = Math.sin(th);
    const nl = Math.hypot(1, slope), n = [c / nl, slope / nl, s / nl];
    pos.push(c * r, -h, s * r); nrm.push(...n); uv.push(u, 1);
    pos.push(c * r * topFactor, h, s * r * topFactor); nrm.push(...n); uv.push(u, 0);
  }
  for (let j = 0; j < seg; j++) {
    const a = j * 2;
    idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
  }
  for (const [y, ny, rf] of [[-h, -1, 1], [h, 1, topFactor]] as const) {
    if (rf === 0) continue;
    const c0 = pos.length / 3; pos.push(0, y, 0); nrm.push(0, ny, 0); uv.push(0.5, 0.5);
    for (let j = 0; j <= seg; j++) {
      const th = (j / seg) * Math.PI * 2;
      pos.push(Math.cos(th) * r * rf, y, Math.sin(th) * r * rf); nrm.push(0, ny, 0); uv.push(0.5, 0.5);
    }
    for (let j = 0; j < seg; j++) {
      if (ny > 0) idx.push(c0, c0 + 1 + j + 1, c0 + 1 + j); else idx.push(c0, c0 + 1 + j, c0 + 1 + j + 1);
    }
  }
  return build(pos, nrm, uv, idx);
}

export function meshGeometry(kind: MeshKind, size: number): Geometry {
  switch (kind) {
    case "cube": return cube(size);
    case "sphere": return sphere(size);
    case "torus": return torus(size);
    case "cylinder": return lathe(size, 1);
    case "cone": return lathe(size, 0);
  }
}
